import React, { ReactNode } from "react";
import { useLanguage } from "../../i18n/LanguageContext";
import { SanityGenericDownload } from "../../model/common";
import { urlForDocument, URLParts } from "../../urls";
import { downloadUrl } from "../../utils/cdn";
import Link from "../Link/Link";

export interface InternalLinkProps {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  reference?: URLParts | SanityGenericDownload;
  fragment?: string;
  query?: string;
}

const InternalLink = ({
  children,
  className,
  style,
  reference,
  query,
  fragment
}: InternalLinkProps) => {
  const language = useLanguage();
  const to = internalLinkUrl(reference, query, fragment, language);
  if (!to) {
    return <>{children}</>;
  }
  return (
    <Link style={style} className={className} to={to}>
      {children}
    </Link>
  );
};

export const internalLinkUrl = (
  reference: URLParts | SanityGenericDownload | undefined,
  query: string | undefined,
  fragment: string | undefined,
  language: string | undefined,
  pathname: string | undefined = undefined
) => {
  // These are "internal", but we want the external Sanity CDN URL.
  if (reference && reference._type === "genericDownload") {
    const genericDownload = reference as SanityGenericDownload;
    // Select an alternative asset by language if one is available.
    const alternative =
      language && language !== "en"
        ? genericDownload.alternatives?.find(
            alterative => alterative.language === language
          )
        : undefined;
    const asset = alternative?.file?.asset ?? genericDownload.file.asset;
    return downloadUrl(asset, {
      overrideFilename: genericDownload.name,
      showInline: genericDownload.showInline
    });
  }
  const suffix = (query ? `?${query}` : "") + (fragment ? `#${fragment}` : "");
  if (reference) {
    const url = urlForDocument(reference as URLParts);
    const isAnchorLink = url.split("#")[0] === pathname?.split("#")[0];
    return `${isAnchorLink ? "" : url}${suffix}`;
  }
  return suffix;
};

export default InternalLink;
